import { Fragment, useMemo } from "react"
import { Link } from "@tanstack/react-router"

import { cn } from "@productlane/lib/src/browser"
import { formatRevenue } from "@productlane/lib/src/format-revenue"
import { Empty, IssueStatusIcon, Tooltip } from "@productlane/ui"

import type { ReportingQueryProps } from "@/screens/app/reporting/reporting-query"
import EmptyIssue from "@/assets/empty-states/empty-issue.png"
import { EntityFeedbackChart } from "@/components/entity-feedback-chart"
import { ReportingSectionHeader } from "@/components/reporting-section-header"
import { api } from "@/lib/api"
import { useDetailLinkFactory } from "@/lib/detail-drawer"
import { useZeroWorkspace } from "@/lib/zero"

type IssuesVisualizationProps = ReportingQueryProps

const statusSorting: Record<string, number> = {
	triage: 0,
	started: 1,
	unstarted: 2,
	backlog: 3,
}

export const IssuesVisualization = ({
	startDate,
	endDate,
	...filters
}: IssuesVisualizationProps) => {
	const workspace = useZeroWorkspace()
	const detailLink = useDetailLinkFactory()

	const issuesMetricsQuery = api.workspace.getIssuesMetrics.useQuery(
		{ startDate, endDate, ...filters },
		{
			cacheTime: Infinity,
			staleTime: Infinity,
		},
	)

	const { groupedIssues, statusInfoMap } = useMemo(() => {
		const issues = (issuesMetricsQuery.data?.issues || []).filter(
			(issue) =>
				issue.feedbackDates.length > 0 &&
				issue.status !== "canceled" &&
				issue.status !== "completed" &&
				issue.status !== "closed",
		)
		const grouped = new Map<string, typeof issues>()
		const statusInfo = new Map<
			string,
			{ statusColor: string | null; statusName: string | null; status: string }
		>()

		for (const issue of issues) {
			const statusKey = issue.statusName || issue.status
			if (!grouped.has(statusKey)) {
				grouped.set(statusKey, [])
				statusInfo.set(statusKey, {
					statusColor: issue.statusColor,
					statusName: issue.statusName,
					status: issue.status,
				})
			}
			grouped.get(statusKey)!.push(issue)
		}

		for (const [, issueList] of grouped) {
			issueList.sort((a, b) => b.painLevel - a.painLevel)
		}

		return {
			groupedIssues: grouped,
			statusInfoMap: statusInfo,
		}
	}, [issuesMetricsQuery.data])

	const orderedStatuses = Array.from(groupedIssues.keys()).sort((a, b) => {
		const aInfo = statusInfoMap.get(a)
		const bInfo = statusInfoMap.get(b)
		const aStatus = aInfo?.status ?? "backlog"
		const bStatus = bInfo?.status ?? "backlog"
		const aOrder = statusSorting[aStatus] ?? Infinity
		const bOrder = statusSorting[bStatus] ?? Infinity
		return aOrder - bOrder
	})

	const hasIssues = orderedStatuses.length > 0 || issuesMetricsQuery.isLoading

	return (
		<>
			{hasIssues ? (
				<div>
					{orderedStatuses.map((statusKey) => {
						const issues = groupedIssues.get(statusKey) || []
						const info = statusInfoMap.get(statusKey)
						const statusColor = info?.statusColor || "#6b7280"
						const totalPainLevel = issues.reduce(
							(sum, i) => sum + i.painLevel,
							0,
						)
						const maxPainInGroup = Math.max(
							...issues.map((i) => i.painLevel),
							0,
						)

						return (
							<Fragment key={statusKey}>
								<ReportingSectionHeader
									icon={
										<IssueStatusIcon
											status={info?.status || ""}
											color={statusColor}
											className="h-3.5 w-3.5"
										/>
									}
									title={info?.statusName || statusKey}
									count={totalPainLevel}
								/>
								<div>
									{issues.map((issue) => {
										const relativePainLevel =
											maxPainInGroup > 0 ? issue.painLevel / maxPainInGroup : 0
										const painLevelInPx = Math.round(relativePainLevel * 40)

										return (
											<Link
												key={issue.id}
												className={cn(
													"flex items-center gap-[1.2rem] px-5 py-2 text-sm hover:bg-gray-50 sm:px-6 dark:hover:bg-gray-850",
												)}
												{...detailLink("issue", issue.id)}
											>
												<div className="flex min-w-[6rem] flex-1 items-center gap-3 truncate">
													<span className="flex-none text-xs text-gray-500">
														{issue.identifier}
													</span>
													<span className="truncate font-medium">
														{issue.title}
													</span>
												</div>

												<div className="w-16 flex-none md:w-32">
													<EntityFeedbackChart
														feedbackDates={issue.feedbackDates}
														color={statusColor}
														startDate={startDate}
														endDate={endDate}
													/>
												</div>

												<div className="ml-2 flex w-auto flex-none items-center gap-2 text-gray-700 md:ml-6 md:w-20 dark:text-gray-300">
													<div className="relative hidden h-1 w-10 rounded bg-gray-200 md:block dark:bg-gray-800">
														<div
															className="absolute h-1 rounded"
															style={{
																width: `${painLevelInPx}px`,
																backgroundColor: statusColor,
															}}
														/>
													</div>

													<p className="w-7">{issue.painLevel}</p>
												</div>

												{workspace && (
													<Tooltip label="Aggregated Revenue">
														<div className="ml-2 flex w-10 flex-none items-center text-gray-700 dark:text-gray-300">
															<p className="text-sm">
																{formatRevenue({
																	revenue: issue.aggregatedRevenue,
																	revenueFormatting:
																		workspace.revenueFormatting,
																	currency: workspace.revenueCurrency,
																})}
															</p>
														</div>
													</Tooltip>
												)}
											</Link>
										)
									})}
								</div>
							</Fragment>
						)
					})}
				</div>
			) : (
				<div className="flex justify-center py-12">
					<Empty
						title="Issues"
						text="Discover which issues customers mention most to prioritize your backlog. No issues in this date range."
						image={
							<img
								width={130}
								height={128}
								src={EmptyIssue}
								alt="Empty issues"
							/>
						}
					/>
				</div>
			)}
		</>
	)
}
